import React, { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";

interface ThumbnailRailProps {
  doc: PDFDocumentProxy;
  totalPages: number;
  currentPage: number;
  onJump: (page: number) => void;
}

/** A single lazily-rendered page thumbnail. */
const Thumbnail: React.FC<{
  doc: PDFDocumentProxy;
  pageNumber: number;
  active: boolean;
  onJump: (page: number) => void;
}> = ({ doc, pageNumber, active, onJump }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { root: el.closest(".thumb-rail"), rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await doc.getPage(pageNumber);
        if (cancelled) return;
        const targetWidth = 120;
        const base = page.getViewport({ scale: 1 });
        const scale = targetWidth / base.width;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch {
        /* cancelled, or a malformed page — leave the thumbnail blank */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, doc, pageNumber]);

  return (
    <button
      ref={wrapRef}
      className={`thumb ${active ? "thumb-active" : ""}`}
      onClick={() => onJump(pageNumber)}
    >
      <div className="thumb-canvas-wrap">
        <canvas ref={canvasRef} />
      </div>
      <span className="thumb-label">{pageNumber}</span>
    </button>
  );
};

const ThumbnailRail: React.FC<ThumbnailRailProps> = ({
  doc,
  totalPages,
  currentPage,
  onJump,
}) => {
  return (
    <div className="thumb-rail">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
        <Thumbnail
          key={n}
          doc={doc}
          pageNumber={n}
          active={n === currentPage}
          onJump={onJump}
        />
      ))}
    </div>
  );
};

export default ThumbnailRail;
